import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PartnerMaintenanceService } from './partner-maintenance.service';
import { parseAsLocalDate } from '../../../shared/_utils/date-utils';
import * as _ from 'lodash-es';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-partner-maintenance',
    templateUrl: './partner-maintenance.component.html',
    styleUrls: ['./partner-maintenance.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PartnerMaintenanceComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  private readonly subs = new Subscription();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  // Component data properties
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  SERVER_CONFIG: any = {};

  // Modal properties
  modalTitle = '';
  
  // Loading states
  loading = true;
  ptrLoading = false;
  showFormLoader = false;

  // Error handling
  HAS_ERROR = false;
  ERROR_MESSAGE: string | null = null;
  genericErrMsg = 'Error!';

  // Partner list properties
  partnerList: any[] = [];
  partnerListOrig: any[] = [];
  partnerListToAdd: any[] = [];

  // Lists for save operations
  ListOfRowsToSave: any[] = [];
  ListOfRowsToAdd: any[] = [];
  ListOfRowsToEdit: any[] = [];
  ListOfRowsToDelete: any[] = [];

  // Eligible partner lists
  eligiblePartnerList: any[] = [];
  eligiblePartnerList1: any[] = [];
  missingPartnersList: any[] = [];
  externalEntitiesList: any[] = [];
  goldEntitiesList: any[] = [];

  // Total ownership percentages
  totalEffectiveOwnershipPercentage = 0;
  totalEffectiveOwnershipPercentageOrig = 0;
  totalEffectiveOwnershipPercentageToAdd = 0;
  totalEffectiveOwnershipPercentageToShow = 0;
  totalEffectiveOwnershipPercentageToShowOrig = 0;

  // Selection objects
  selMissingPtr: any = {};
  selExtEntity: any = {};
  selGoldEntity: any = {};

  // Partnership details
  PSHIP_LEID: string = '';
  PSHIP_NAME: string = '';
  PSHIP_REPORTING_PERIOD: string = '';
  PSHIP_US_TAX_TYPE: string = '';
  PSHIP_BEGIN_DATE: string = '';
  PSHIP_END_DATE: string = '';
  INT_EXT: string = '';

  // User settings
  userType: any;
  logged_in_user: string = '';
  scenarioLabel: string = '';

  // Misc
  add = 'n';
  emptyPartnerLabel = '';
  partnerCount = 0;
  tooltipMsg = "Add New Partner";
  ptrCanBeRemoved: boolean = false;

  // Date options
  dateOptions = {
    dateDisabled: false,
    formatYear: 'yyyy',
    startingDay: 1,
    showWeeks: false
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private _snackBar: MatSnackBar,
    private partnerMaintenanceService: PartnerMaintenanceService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
    this.filterParams = normalizeFilterParams(activeScreen);
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0 && this.filterParams && Object.keys(this.filterParams).length > 0) {
        console.log('this.filterParams=',this.filterParams);
        this.setupPartnershipData();
        this.loadPartnerData();
      }
    } catch (e) {
      console.error('Error in partner-maintenance init:', e);
    }
  }

  private setupPartnershipData() {
    // Partnership details from rowData
    this.PSHIP_LEID = this.rowData.LEID;
    this.PSHIP_NAME = this.rowData.LE_NAME;
    this.PSHIP_REPORTING_PERIOD = this.rowData.REPORTING_PERIOD;
    this.PSHIP_US_TAX_TYPE = this.rowData.TAX_TYPE;
    this.PSHIP_BEGIN_DATE = this.rowData.TAX_YEAR_BEGIN;
    this.PSHIP_END_DATE = this.rowData.TAX_YEAR_END;
    this.INT_EXT = (this.rowData.RETURN_PREPARED_BY == 'Internal') ? '1' : '0';

    // User settings
    this.userType = this.USER_SETTINGS?.user?.client?.user_type_key;
    this.logged_in_user = this.USER_SETTINGS?.user?.sso_id || '';

    // Build modal title
    this.scenarioLabel = this.globalService?.scenario_desc || this.filterParams.scenario || '';
    this.modalTitle = `Partnership/Partner Setup Details ( Tax Year: ${this.filterParams.tax_year} - Scenario: ${this.scenarioLabel} )`;
  }

  private loadPartnerData() {
    const params = {
      action_code: '80ghdk',
      tax_year: this.filterParams.tax_year,
      scenario: this.filterParams.scenario,
      jcd_key: this.globalService?.jcd_key || 250,
      leid: this.PSHIP_LEID,
      rp: this.PSHIP_REPORTING_PERIOD
    };

    const params1 = {
      action_code: 'kenbvi',
      tax_year: this.filterParams.tax_year,
      scenario: this.filterParams.scenario,
      jcd_key: this.globalService?.jcd_key || 250,
      leid: this.PSHIP_LEID,
      rp: this.PSHIP_REPORTING_PERIOD
    };

    this.partnerMaintenanceService.loadPartnerList(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data && data.jsonObject) {
          this.partnerList = data.jsonObject;
          this.partnerCount = this.partnerList.length;

          this.totalEffectiveOwnershipPercentage = 0;
          this.totalEffectiveOwnershipPercentageOrig = 0;
          this.totalEffectiveOwnershipPercentageToAdd = 0;

          for (let i = 0; i < this.partnerList.length; i++) {
            this.ptrCanBeRemoved = (this.partnerList[i].CAN_DELETE_PTR == 1);

            this.partnerList[i]['view'] = true;
            this.partnerList[i]['edit'] = false;
            this.partnerList[i]['delete'] = this.ptrCanBeRemoved;
            this.partnerList[i]['is_dirty'] = false;
            this.partnerList[i]['ptr_sel_err_msg'] = null;
            this.partnerList[i]['eff_own_err_msg'] = null;
            this.partnerList[i]['beg_date_err_msg'] = null;
            this.partnerList[i]['end_date_err_msg'] = null;
            this.partnerList[i]['has_ptr_sel_err'] = false;
            this.partnerList[i]['has_ownership_err'] = false;
            this.partnerList[i]['has_beg_date_err'] = false;
            this.partnerList[i]['has_end_date_err'] = false;
            this.partnerList[i]['calendar'] = { opened: false };
            this.partnerList[i]['calendar1'] = { opened: false };

            this.totalEffectiveOwnershipPercentage += parseFloat(this.partnerList[i].EFFECTIVE_OWNERSHIP_PERCENTAGE) || 0;
            this.totalEffectiveOwnershipPercentageOrig += parseFloat(this.partnerList[i].EFFECTIVE_OWNERSHIP_PERCENTAGE) || 0;
          }

          this.partnerListOrig = _.cloneDeep(this.partnerList);
          this.totalEffectiveOwnershipPercentageToShow = this.totalEffectiveOwnershipPercentage;
          this.totalEffectiveOwnershipPercentageToShowOrig = this.totalEffectiveOwnershipPercentageToShow;

          this.emptyPartnerLabel = 'No partner(s) are setup.';

          // Load eligible partners
          this.loadEligiblePartners(params1);
        }
      },
      (error: any) => {
        console.error('Error loading partner list:', error);
        this.loading = false;
        this._snackBar.open('Error loading partner list. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  private loadEligiblePartners(params: any) {
    this.partnerMaintenanceService.loadPartnerList(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data && data.jsonObject) {
          this.eligiblePartnerList = data.jsonObject;

          this.eligiblePartnerList1 = [];
          this.missingPartnersList = this.eligiblePartnerList.filter((item: any) => item.PARTNER_SECTION === "missing_partners");
          this.externalEntitiesList = this.eligiblePartnerList.filter((item: any) => item.PARTNER_SECTION === "external_entities");
          this.goldEntitiesList = this.eligiblePartnerList.filter((item: any) => item.PARTNER_SECTION === "gold_entities");

          this.selMissingPtr['PTR_LEID_RP'] = '0';
          this.selMissingPtr['PTR_LE'] = '------ Choose Missing Partner ------';

          this.selExtEntity['PTR_LEID_RP'] = '1';
          this.selExtEntity['PTR_LE'] = '------ Choose External Entity ------';

          this.selGoldEntity['PTR_LEID_RP'] = '2';
          this.selGoldEntity['PTR_LE'] = '------ Choose GOLD Entity ------';

          if (this.missingPartnersList.length > 0) {
            this.eligiblePartnerList1.push(this.selMissingPtr);
            for (let i = 0; i < this.missingPartnersList.length; i++) {
              this.eligiblePartnerList1.push(this.missingPartnersList[i]);
            }
          }

          if (this.externalEntitiesList.length > 0) {
            this.eligiblePartnerList1.push(this.selExtEntity);
            for (let i = 0; i < this.externalEntitiesList.length; i++) {
              this.eligiblePartnerList1.push(this.externalEntitiesList[i]);
            }
          }

          if (this.goldEntitiesList.length > 0) {
            this.eligiblePartnerList1.push(this.selGoldEntity);
            for (let i = 0; i < this.goldEntitiesList.length; i++) {
              this.eligiblePartnerList1.push(this.goldEntitiesList[i]);
            }
          }

          this.loading = false;
        }
      },
      (error: any) => {
        console.error('Error loading eligible partners:', error);
        this.loading = false;
        this._snackBar.open('Error loading eligible partners. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  showYesNo(value: any): string {
    if (value == "1" || value === 1 || value === true) return "Yes";
    if (value == "0" || value === 0 || value === false) return "No";
    return "";
  }

  openCalendar(index: number) {
    const row = this.partnerListToAdd[index];
    row['calendar'].opened = true;
  }

  openCalendar1(index: number) {
    const row = this.partnerListToAdd[index];
    row['calendar1'].opened = true;
  }

  openCalendarE(index: number) {
    const row = this.partnerList[index];
    row['calendar'].opened = true;
  }

  openCalendar1E(index: number) {
    const row = this.partnerList[index];
    row['calendar1'].opened = true;
  }

  loadPartnerAttributes(ptrLeidRp: string, index: number) {
    const ptr_leid = ptrLeidRp.substr(0, 6);
    const ptr_rp = ptrLeidRp.substr(7);

    const params = {
      action_code: 'm2yzyy',
      tax_year: this.filterParams.tax_year,
      scenario: this.filterParams.scenario,
      jcd_key: this.globalService?.jcd_key || 250,
      leid: this.PSHIP_LEID,
      rp: this.PSHIP_REPORTING_PERIOD,
      ptr_leid: ptr_leid,
      ptr_rp: ptr_rp
    };

    this.partnerMaintenanceService.loadPartnerList(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data && data.jsonObject && data.jsonObject.length > 0) {
          const row = this.partnerListToAdd[index];
          const ptrAttributes = data.jsonObject[0];

          row['PARTNER_LEID'] = ptrAttributes.PTR_LEID;
          row['PARTNER_REPORTING_PERIOD'] = ptrAttributes.PTR_RP;
          row['PTR_LE'] = `${ptrAttributes.PTR_LEID}/${ptrAttributes.PTR_RP}/${ptrAttributes.LE_NAME}`;
          row['PARTNER_NAME'] = ptrAttributes.LE_NAME;
          row['PARTNER_US_TAX_TYPE'] = ptrAttributes.TAX_TYPE;

          if (ptrAttributes.EFFECTIVE_OWNERSHIP != null) {
            row['EFFECTIVE_OWNERSHIP_PERCENTAGE'] = this.roundToSix(ptrAttributes.EFFECTIVE_OWNERSHIP);
          } else {
            row['EFFECTIVE_OWNERSHIP_PERCENTAGE'] = 0;
          }

          // Format dates for HTML5 date inputs (YYYY-MM-DD format)
          row['EFFECTIVE_DATE_BEGIN'] = this.formatDateForInput(ptrAttributes.PTR_TAX_YEAR_BEGIN);
          row['EFFECTIVE_DATE_END'] = this.formatDateForInput(ptrAttributes.PTR_TAX_YEAR_END);
          row['IS_PTR_INACTIVE'] = ptrAttributes.IS_PTR_INACTIVE;
          row['PTR_K1_INITIAL'] = ptrAttributes.PTR_K1_INITIAL;
          row['PTR_K1_FINAL'] = ptrAttributes.PTR_K1_FINAL;

          this.totalEffectiveOwnershipPercentageToAdd += parseFloat(row['EFFECTIVE_OWNERSHIP_PERCENTAGE']) || 0;
          this.totalEffectiveOwnershipPercentageToShow = this.roundToSix(this.totalEffectiveOwnershipPercentage + this.totalEffectiveOwnershipPercentageToAdd);
        }
      },
      (error: any) => {
        console.error('Error loading partner attributes:', error);
        this._snackBar.open('Error loading partner attributes. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  addPartner() {
    const partner: any = {};
    const calendarState = { opened: false };
    const calendar1State = { opened: false };

    this.add = 'y';
    partner['PARTNER_LEID'] = '';
    partner['PARTNER_REPORTING_PERIOD'] = '';

    if (this.missingPartnersList.length > 0) {
      partner['PTR_LE'] = '------ Choose Missing Partner ------';
    } else if (this.externalEntitiesList.length > 0) {
      partner['PTR_LE'] = '------ Choose External Entity ------';
    } else if (this.goldEntitiesList.length > 0) {
      partner['PTR_LE'] = '------ Choose GOLD Entity ------';
    }

    partner['PARTNER_NAME'] = '';
    partner['PARTNER_US_TAX_TYPE'] = '';
    partner['EFFECTIVE_OWNERSHIP_PERCENTAGE'] = 0.00;

    partner['EFFECTIVE_DATE_BEGIN'] = null;
    partner['calendar'] = calendarState;

    partner['EFFECTIVE_DATE_END'] = null;
    partner['calendar1'] = calendar1State;

    partner['IS_PTR_INACTIVE'] = 0;
    partner['PTR_K1_INITIAL'] = 0;
    partner['PTR_K1_FINAL'] = 0;

    partner['ptr_sel_err_msg'] = null;
    partner['eff_own_err_msg'] = null;
    partner['beg_date_err_msg'] = null;
    partner['end_date_err_msg'] = null;

    partner['has_ptr_sel_err'] = false;
    partner['has_ownership_err'] = false;
    partner['has_beg_date_err'] = false;
    partner['has_end_date_err'] = false;

    this.partnerListToAdd.push(partner);
  }

  editExistingPartner(index: number) {
    this.partnerList[index].view = !this.partnerList[index].view;
    this.partnerList[index].edit = !this.partnerList[index].edit;

    if (this.partnerList[index].CAN_DELETE_PTR == 1) {
      this.partnerList[index].delete = !this.partnerList[index].delete;
    } else {
      this.partnerList[index].delete = false;
    }

    if (this.partnerList[index].view == false && this.partnerList[index].edit == true) {
      const beginDate = this.partnerList[index]['EFFECTIVE_DATE_BEGIN'];
      const endDate = this.partnerList[index]['EFFECTIVE_DATE_END'];

      // Format dates for HTML5 date inputs (YYYY-MM-DD format)
      this.partnerList[index]['EFFECTIVE_DATE_BEGIN'] = this.formatDateForInput(beginDate);
      this.partnerList[index]['EFFECTIVE_DATE_END'] = this.formatDateForInput(endDate);
    }

    if (this.partnerList[index].view == true && this.partnerList[index].edit == false) {
      let correctIndex = 0;
      const partner_leid = this.partnerList[index]['PARTNER_LEID'];
      const partner_reporting_period = this.partnerList[index]['PARTNER_REPORTING_PERIOD'];

      const beginDate = this.partnerList[index]['EFFECTIVE_DATE_BEGIN'];
      const endDate = this.partnerList[index]['EFFECTIVE_DATE_END'];

      this.partnerList[index]['EFFECTIVE_DATE_BEGIN'] = this.formatDate(beginDate);
      this.partnerList[index]['EFFECTIVE_DATE_END'] = this.formatDate(endDate);

      for (let i = 0; i < this.partnerListOrig.length; i++) {
        if (partner_leid + '/' + partner_reporting_period == this.partnerListOrig[i]['PARTNER_LEID'] + '/' + this.partnerListOrig[i]['PARTNER_REPORTING_PERIOD']) {
          correctIndex = i;
          break;
        }
      }
      this.partnerList[index] = _.cloneDeep(this.partnerListOrig[correctIndex]);
      this.partnerList[index]['is_dirty'] = false;
      this.adjustEffectiveOwnershipPercentage(this.partnerListOrig[index]);
    }
  }

  removeExistingPartner(index: number) {
    const rowToDelete: any = {};
    const ownershipPercentage = parseFloat(this.partnerList[index]['EFFECTIVE_OWNERSHIP_PERCENTAGE']) || 0;
    this.totalEffectiveOwnershipPercentage -= ownershipPercentage;
    this.totalEffectiveOwnershipPercentageToShow = this.roundToSix(this.totalEffectiveOwnershipPercentage + this.totalEffectiveOwnershipPercentageToAdd);

    rowToDelete['mode'] = 'delete';
    rowToDelete['leid'] = this.PSHIP_LEID;
    rowToDelete['reporting_period'] = this.PSHIP_REPORTING_PERIOD;
    rowToDelete['ptr_leid'] = this.partnerList[index].PARTNER_LEID;
    rowToDelete['ptr_reporting_period'] = this.partnerList[index].PARTNER_REPORTING_PERIOD;
    this.ListOfRowsToDelete.push(rowToDelete);

    this.partnerList.splice(index, 1);
  }

  removePartnerToAdd(index: number) {
    const ownershipPercentage = parseFloat(this.partnerListToAdd[index]['EFFECTIVE_OWNERSHIP_PERCENTAGE']) || 0;
    this.totalEffectiveOwnershipPercentageToAdd -= ownershipPercentage;
    this.totalEffectiveOwnershipPercentageToShow = this.roundToSix(this.totalEffectiveOwnershipPercentage + this.totalEffectiveOwnershipPercentageToAdd);

    if (this.partnerListToAdd[index]['ptr_sel_err_msg'] != null || this.partnerListToAdd[index]['eff_own_err_msg'] != null || 
        this.partnerListToAdd[index]['beg_date_err_msg'] != null || this.partnerListToAdd[index]['end_date_err_msg'] != null) {
      this.HAS_ERROR = false;
      this.ERROR_MESSAGE = null;
    }

    this.partnerListToAdd.splice(index, 1);
  }

  mark_dirty(index: number) {
    this.partnerList[index]['is_dirty'] = true;
  }

  handleOwnershipBlur(row: any) {
    // Set to 0 if empty, null, or undefined
    if (row.EFFECTIVE_OWNERSHIP_PERCENTAGE === undefined || 
        row.EFFECTIVE_OWNERSHIP_PERCENTAGE === null || 
        row.EFFECTIVE_OWNERSHIP_PERCENTAGE === '') {
      row.EFFECTIVE_OWNERSHIP_PERCENTAGE = 0;
    } else {
      // Round to 6 decimal places
      row.EFFECTIVE_OWNERSHIP_PERCENTAGE = this.roundToSix(row.EFFECTIVE_OWNERSHIP_PERCENTAGE);
    }
    this.adjustEffectiveOwnershipPercentage(row);
  }

  adjustEffectiveOwnershipPercentage(row: any) {
    this.totalEffectiveOwnershipPercentage = 0.000000;
    this.totalEffectiveOwnershipPercentageToAdd = 0.000000;

    for (let i = 0; i < this.partnerList.length; i++) {
      if (this.partnerList[i].EFFECTIVE_OWNERSHIP_PERCENTAGE === undefined || 
          this.partnerList[i].EFFECTIVE_OWNERSHIP_PERCENTAGE === null || 
          this.partnerList[i].EFFECTIVE_OWNERSHIP_PERCENTAGE === '') {
        this.partnerList[i].EFFECTIVE_OWNERSHIP_PERCENTAGE = 0;
      }
      this.totalEffectiveOwnershipPercentage += parseFloat(this.partnerList[i].EFFECTIVE_OWNERSHIP_PERCENTAGE) || 0;
    }

    for (let i = 0; i < this.partnerListToAdd.length; i++) {
      if (this.partnerListToAdd[i].EFFECTIVE_OWNERSHIP_PERCENTAGE === undefined || 
          this.partnerListToAdd[i].EFFECTIVE_OWNERSHIP_PERCENTAGE === null || 
          this.partnerListToAdd[i].EFFECTIVE_OWNERSHIP_PERCENTAGE === '') {
        this.partnerListToAdd[i].EFFECTIVE_OWNERSHIP_PERCENTAGE = 0;
      }
      this.totalEffectiveOwnershipPercentageToAdd += parseFloat(this.partnerListToAdd[i].EFFECTIVE_OWNERSHIP_PERCENTAGE) || 0;
    }

    this.totalEffectiveOwnershipPercentageToShow = this.roundToSix(this.totalEffectiveOwnershipPercentage + this.totalEffectiveOwnershipPercentageToAdd);
  }

  save() {
    this.ListOfRowsToAdd = [];
    this.ListOfRowsToEdit = [];
    this.ListOfRowsToSave = [];
    let continueSave = true;
    let ptrListToDelete = '';

    this.isSaveClicked = true;

    // Process partnerListToAdd
    for (let i = 0; i < this.partnerListToAdd.length; i++) {
      const rowToAdd: any = {};
      const partnerRow = this.partnerListToAdd[i];
      
      rowToAdd['mode'] = 'add';
      rowToAdd['leid'] = this.PSHIP_LEID;
      rowToAdd['reporting_period'] = this.PSHIP_REPORTING_PERIOD;
      rowToAdd['int_ext'] = this.INT_EXT;
      rowToAdd['ptr_leid'] = partnerRow.PARTNER_LEID;
      rowToAdd['ptr_reporting_period'] = partnerRow.PARTNER_REPORTING_PERIOD;
      // Ensure allocation_ratio is 0 if empty, null, or undefined
      rowToAdd['allocation_ratio'] = (partnerRow.EFFECTIVE_OWNERSHIP_PERCENTAGE === undefined || 
                                       partnerRow.EFFECTIVE_OWNERSHIP_PERCENTAGE === null || 
                                       partnerRow.EFFECTIVE_OWNERSHIP_PERCENTAGE === '') ? 0 : partnerRow.EFFECTIVE_OWNERSHIP_PERCENTAGE;

      const beginDate = partnerRow.EFFECTIVE_DATE_BEGIN;
      const endDate = partnerRow.EFFECTIVE_DATE_END;

      if (beginDate != null) {
        rowToAdd['tax_year_begin'] = this.formatDate(beginDate);
      } else {
        rowToAdd['tax_year_begin'] = '';
      }

      if (endDate != null) {
        rowToAdd['tax_year_end'] = this.formatDate(endDate);
      } else {
        rowToAdd['tax_year_end'] = '';
      }

      rowToAdd['ptr_k1_initial'] = partnerRow.PTR_K1_INITIAL;
      rowToAdd['ptr_k1_final'] = partnerRow.PTR_K1_FINAL;
      rowToAdd['is_ptr_inactive'] = partnerRow.IS_PTR_INACTIVE;

      rowToAdd['ptr_sel_err_msg'] = null;
      rowToAdd['eff_own_err_msg'] = null;
      rowToAdd['beg_date_err_msg'] = null;
      rowToAdd['end_date_err_msg'] = null;

      rowToAdd['has_ptr_sel_err'] = false;
      rowToAdd['has_ownership_err'] = false;
      rowToAdd['has_beg_date_err'] = false;
      rowToAdd['has_end_date_err'] = false;

      this.ListOfRowsToAdd.push(rowToAdd);
    }

    // Process partnerList for edits
    for (let i = 0; i < this.partnerList.length; i++) {
      const rowToEdit: any = {};
      const partnerRow = this.partnerList[i];

      if (partnerRow.view == false && partnerRow.edit == true && partnerRow.is_dirty == true) {
        rowToEdit['mode'] = 'edit';
        rowToEdit['leid'] = this.PSHIP_LEID;
        rowToEdit['reporting_period'] = this.PSHIP_REPORTING_PERIOD;
        rowToEdit['ptr_leid'] = partnerRow.PARTNER_LEID;
        rowToEdit['ptr_reporting_period'] = partnerRow.PARTNER_REPORTING_PERIOD;
        // Ensure allocation_ratio is 0 if empty, null, or undefined
        rowToEdit['allocation_ratio'] = (partnerRow.EFFECTIVE_OWNERSHIP_PERCENTAGE === undefined || 
                                          partnerRow.EFFECTIVE_OWNERSHIP_PERCENTAGE === null || 
                                          partnerRow.EFFECTIVE_OWNERSHIP_PERCENTAGE === '') ? 0 : partnerRow.EFFECTIVE_OWNERSHIP_PERCENTAGE;

        const beginDate = partnerRow.EFFECTIVE_DATE_BEGIN;
        const endDate = partnerRow.EFFECTIVE_DATE_END;

        rowToEdit['tax_year_begin'] = this.formatDate(beginDate);
        rowToEdit['tax_year_end'] = this.formatDate(endDate);

        rowToEdit['ptr_k1_initial'] = partnerRow.PTR_K1_INITIAL;
        rowToEdit['ptr_k1_final'] = partnerRow.PTR_K1_FINAL;
        rowToEdit['is_ptr_inactive'] = partnerRow.IS_PTR_INACTIVE;

        rowToEdit['eff_own_err_msg'] = null;
        rowToEdit['beg_date_err_msg'] = null;
        rowToEdit['end_date_err_msg'] = null;

        rowToEdit['has_ownership_err'] = false;
        rowToEdit['has_beg_date_err'] = false;
        rowToEdit['has_end_date_err'] = false;

        this.ListOfRowsToEdit.push(rowToEdit);
      }
    }

    // Combine all lists
    for (let i = 0; i < this.ListOfRowsToAdd.length; i++) {
      this.ListOfRowsToSave.push(this.ListOfRowsToAdd[i]);
    }

    for (let i = 0; i < this.ListOfRowsToEdit.length; i++) {
      this.ListOfRowsToSave.push(this.ListOfRowsToEdit[i]);
    }

    for (let i = 0; i < this.ListOfRowsToDelete.length; i++) {
      this.ListOfRowsToSave.push(this.ListOfRowsToDelete[i]);
    }

    // If nothing to save, save all existing partners
    if (this.ListOfRowsToSave.length == 0) {
      for (let i = 0; i < this.partnerList.length; i++) {
        const rowToAdd: any = {};
        const partnerRow = this.partnerList[i];
        
        rowToAdd['mode'] = 'add';
        rowToAdd['leid'] = this.PSHIP_LEID;
        rowToAdd['reporting_period'] = this.PSHIP_REPORTING_PERIOD;
        rowToAdd['int_ext'] = this.INT_EXT;
        rowToAdd['ptr_leid'] = partnerRow.PARTNER_LEID;
        rowToAdd['ptr_reporting_period'] = partnerRow.PARTNER_REPORTING_PERIOD;
        // Ensure allocation_ratio is 0 if empty, null, or undefined
        rowToAdd['allocation_ratio'] = (partnerRow.EFFECTIVE_OWNERSHIP_PERCENTAGE === undefined || 
                                         partnerRow.EFFECTIVE_OWNERSHIP_PERCENTAGE === null || 
                                         partnerRow.EFFECTIVE_OWNERSHIP_PERCENTAGE === '') ? 0 : partnerRow.EFFECTIVE_OWNERSHIP_PERCENTAGE;

        const beginDate = partnerRow.EFFECTIVE_DATE_BEGIN;
        const endDate = partnerRow.EFFECTIVE_DATE_END;

        if (beginDate != null) {
          rowToAdd['tax_year_begin'] = beginDate;
        } else {
          rowToAdd['tax_year_begin'] = '';
        }

        if (endDate != null) {
          rowToAdd['tax_year_end'] = endDate;
        } else {
          rowToAdd['tax_year_end'] = '';
        }

        rowToAdd['ptr_k1_initial'] = partnerRow.PTR_K1_INITIAL;
        rowToAdd['ptr_k1_final'] = partnerRow.PTR_K1_FINAL;
        rowToAdd['is_ptr_inactive'] = partnerRow.IS_PTR_INACTIVE;

        rowToAdd['ptr_sel_err_msg'] = null;
        rowToAdd['eff_own_err_msg'] = null;
        rowToAdd['beg_date_err_msg'] = null;
        rowToAdd['end_date_err_msg'] = null;

        rowToAdd['has_ptr_sel_err'] = false;
        rowToAdd['has_ownership_err'] = false;
        rowToAdd['has_beg_date_err'] = false;
        rowToAdd['has_end_date_err'] = false;

        this.ListOfRowsToAdd.push(rowToAdd);
      }

      for (let i = 0; i < this.ListOfRowsToAdd.length; i++) {
        this.ListOfRowsToSave.push(this.ListOfRowsToAdd[i]);
      }
    }

    if (this.ListOfRowsToSave.length > 0) {
      const validationStat = this.validateSetupData();

      if (validationStat) {
        if (this.ListOfRowsToDelete.length > 0) {
          for (let i = 0; i < this.ListOfRowsToDelete.length; i++) {
            ptrListToDelete += this.ListOfRowsToDelete[i]['ptr_leid'] + '/' + this.ListOfRowsToDelete[i]['ptr_reporting_period'] + '\n';
          }

          if (confirm("The following partner(s) will be removed. Do you want to Proceed? Please confirm.\n" + ptrListToDelete)) {
            continueSave = true;
          } else {
            continueSave = false;
            this.isSaveClicked = false;
          }
        }

        if (continueSave) {
          const returnClobSettingsObj: any = {};
          returnClobSettingsObj['tax_year'] = this.filterParams.tax_year;
          returnClobSettingsObj['scenario'] = this.filterParams.scenario;
          returnClobSettingsObj['jcd_key'] = this.globalService?.jcd_key || 250;
          returnClobSettingsObj['sso_id'] = this.logged_in_user;

          const message = 'Partner setup saved successfully.';
          this.sendDetails(returnClobSettingsObj, this.ListOfRowsToSave, message);
        }
      } else {
        this.isSaveClicked = false;
      }
    }
  }

  private sendDetails(returnClobSettingsObj: any, pshipSetupSaveDtls: any[], message: string) {
    this.crudLoading = true;

    this.partnerMaintenanceService.savePartnerSetup(
      this.GENERAL_CONFIG.base_url,
      returnClobSettingsObj,
      pshipSetupSaveDtls,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        // Check for error - handle both result.errorMessage and result.data.errorMessage formats
        const errorMessage = result.errorMessage || result.data?.errorMessage;
        if (errorMessage && errorMessage !== 'null') {
          this._snackBar.open(errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open(message, 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        console.error('Error saving partner setup:', error);
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  private validateSetupData(): boolean {
    this.HAS_ERROR = false;
    this.ERROR_MESSAGE = null;

    // Validate existing partners
    for (let i = 0; i < this.partnerList.length; i++) {
      if (this.partnerList[i]['is_dirty']) {
        this.partnerList[i]['eff_own_err_msg'] = null;
        this.partnerList[i]['has_ownership_err'] = false;
        this.partnerList[i]['beg_date_err_msg'] = null;
        this.partnerList[i]['has_beg_date_err'] = false;
        this.partnerList[i]['end_date_err_msg'] = null;
        this.partnerList[i]['has_end_date_err'] = false;

        if (this.partnerList[i]['EFFECTIVE_OWNERSHIP_PERCENTAGE'] > 100) {
          this.partnerList[i]['eff_own_err_msg'] = 'Ownership must not be more than 100';
          this.partnerList[i]['has_ownership_err'] = true;
          this.ERROR_MESSAGE = this.partnerList[i]['eff_own_err_msg'];
          this.HAS_ERROR = true;
          break;
        } else if (this.compareDatesOnly(this.partnerList[i]['EFFECTIVE_DATE_BEGIN'], this.partnerList[i]['EFFECTIVE_DATE_END']) > 0) {
          this.partnerList[i]['beg_date_err_msg'] = 'Partner Begin date must not be more than Partner End Date';
          this.partnerList[i]['has_beg_date_err'] = true;
          this.ERROR_MESSAGE = this.partnerList[i]['beg_date_err_msg'];
          this.HAS_ERROR = true;
          break;
        } else if (this.compareDatesOnly(this.partnerList[i]['EFFECTIVE_DATE_BEGIN'], this.PSHIP_BEGIN_DATE) < 0) {
          this.partnerList[i]['beg_date_err_msg'] = 'Partner Begin date must not be less than Partnership Begin Date';
          this.partnerList[i]['has_beg_date_err'] = true;
          this.ERROR_MESSAGE = this.partnerList[i]['beg_date_err_msg'];
          this.HAS_ERROR = true;
          break;
        } else if (this.compareDatesOnly(this.partnerList[i]['EFFECTIVE_DATE_END'], this.PSHIP_END_DATE) > 0) {
          this.partnerList[i]['end_date_err_msg'] = 'Partner End date must not be more than Partnership End Date';
          this.partnerList[i]['has_end_date_err'] = true;
          this.ERROR_MESSAGE = this.partnerList[i]['end_date_err_msg'];
          this.HAS_ERROR = true;
          break;
        }
      }
    }

    // Validate new partners to add
    if (!this.HAS_ERROR) {
      for (let i = 0; i < this.partnerListToAdd.length; i++) {
        this.partnerListToAdd[i]['ptr_sel_err_msg'] = null;
        this.partnerListToAdd[i]['has_ptr_sel_err'] = false;
        this.partnerListToAdd[i]['eff_own_err_msg'] = null;
        this.partnerListToAdd[i]['has_ownership_err'] = false;
        this.partnerListToAdd[i]['beg_date_err_msg'] = null;
        this.partnerListToAdd[i]['has_beg_date_err'] = false;
        this.partnerListToAdd[i]['end_date_err_msg'] = null;
        this.partnerListToAdd[i]['has_end_date_err'] = false;

        if (this.partnerListToAdd[i]['PTR_LE'] == '------ Choose Missing Partner ------' || 
            this.partnerListToAdd[i]['PTR_LE'] == '------ Choose External Entity ------' || 
            this.partnerListToAdd[i]['PTR_LE'] == '------ Choose GOLD Entity ------') {
          this.partnerListToAdd[i]['ptr_sel_err_msg'] = 'Please select partner from the list';
          this.partnerListToAdd[i]['has_ptr_sel_err'] = true;
          this.ERROR_MESSAGE = this.partnerListToAdd[i]['ptr_sel_err_msg'];
          this.HAS_ERROR = true;
          break;
        } else {
          if (this.isDuplicatePartner(this.partnerListToAdd[i]['PTR_LE'])) {
            this.partnerListToAdd[i]['ptr_sel_err_msg'] = 'Please select same partner only once';
            this.partnerListToAdd[i]['has_ptr_sel_err'] = true;
            this.ERROR_MESSAGE = this.partnerListToAdd[i]['ptr_sel_err_msg'];
            this.HAS_ERROR = true;
            break;
          }
        }

        if (this.partnerListToAdd[i]['EFFECTIVE_OWNERSHIP_PERCENTAGE'] > 100) {
          this.partnerListToAdd[i]['eff_own_err_msg'] = 'Ownership must not be more than 100';
          this.partnerListToAdd[i]['has_ownership_err'] = true;
          this.ERROR_MESSAGE = this.partnerListToAdd[i]['eff_own_err_msg'];
          this.HAS_ERROR = true;
          break;
        } else if (this.compareDatesOnly(this.partnerListToAdd[i]['EFFECTIVE_DATE_BEGIN'], this.partnerListToAdd[i]['EFFECTIVE_DATE_END']) > 0) {
          this.partnerListToAdd[i]['beg_date_err_msg'] = 'Partner Begin date must not be more than Partner End Date';
          this.partnerListToAdd[i]['has_beg_date_err'] = true;
          this.ERROR_MESSAGE = this.partnerListToAdd[i]['beg_date_err_msg'];
          this.HAS_ERROR = true;
          break;
        } else if (this.compareDatesOnly(this.partnerListToAdd[i]['EFFECTIVE_DATE_BEGIN'], this.PSHIP_BEGIN_DATE) < 0) {
          this.partnerListToAdd[i]['beg_date_err_msg'] = 'Partner Begin date must not be less than Partnership Begin Date';
          this.partnerListToAdd[i]['has_beg_date_err'] = true;
          this.ERROR_MESSAGE = this.partnerListToAdd[i]['beg_date_err_msg'];
          this.HAS_ERROR = true;
          break;
        } else if (this.compareDatesOnly(this.partnerListToAdd[i]['EFFECTIVE_DATE_END'], this.PSHIP_END_DATE) > 0) {
          this.partnerListToAdd[i]['end_date_err_msg'] = 'Partner End date must not be more than Partnership End Date';
          this.partnerListToAdd[i]['has_end_date_err'] = true;
          this.ERROR_MESSAGE = this.partnerListToAdd[i]['end_date_err_msg'];
          this.HAS_ERROR = true;
          break;
        }
      }
    }

    return !this.HAS_ERROR;
  }

  private isDuplicatePartner(ptrLE: string): boolean {
    let ptrSel = 0;
    for (let i = 0; i < this.partnerListToAdd.length; i++) {
      if (ptrLE == this.partnerListToAdd[i]['PTR_LE']) {
        ptrSel++;
      }
    }
    return ptrSel > 1;
  }

  reset() {
    this.partnerList = _.cloneDeep(this.partnerListOrig);
    this.totalEffectiveOwnershipPercentage = this.totalEffectiveOwnershipPercentageOrig;
    this.totalEffectiveOwnershipPercentageToAdd = 0;
    this.totalEffectiveOwnershipPercentageToShow = this.totalEffectiveOwnershipPercentageOrig;
    this.partnerListToAdd = [];

    this.ListOfRowsToSave = [];
    this.ListOfRowsToAdd = [];
    this.ListOfRowsToEdit = [];
    this.ListOfRowsToDelete = [];

    this.HAS_ERROR = false;
    this.ERROR_MESSAGE = null;
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  private formatDate(date: any): string {
    if (!date) return '';
    
    // Use parseAsLocalDate to avoid timezone shifts
    const d = parseAsLocalDate(date);
    if (!d) return '';
    
    const month = ((d.getMonth() > 8) ? (d.getMonth() + 1) : ('0' + (d.getMonth() + 1)));
    const day = ((d.getDate() > 9) ? d.getDate() : ('0' + d.getDate()));
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  }

  private formatDateForInput(dateString: any): string {
    if (!dateString) return '';
    
    // Use parseAsLocalDate to avoid timezone shifts
    const d = parseAsLocalDate(dateString);
    if (!d) return '';
    
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private roundToSix(num: number): number {
    return +(Math.round(Number(num + "e+6")) + "e-6");
  }

  /**
   * Compares two dates by date only (ignoring time component)
   * Uses parseAsLocalDate to avoid timezone-related issues
   * @param date1 First date to compare
   * @param date2 Second date to compare
   * @returns negative if date1 < date2, positive if date1 > date2, 0 if equal
   */
  private compareDatesOnly(date1: any, date2: any): number {
    if (!date1 || !date2) return 0;
    
    // Use parseAsLocalDate to avoid timezone shifts
    const d1 = parseAsLocalDate(date1);
    const d2 = parseAsLocalDate(date2);
    
    if (!d1 || !d2) return 0;
    
    // Normalize to midnight local time to ignore time components
    const normalizedDate1 = new Date(d1.getFullYear(), d1.getMonth(), d1.getDate());
    const normalizedDate2 = new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());
    
    return normalizedDate1.getTime() - normalizedDate2.getTime();
  }
}

